import { Button } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Event } from 'typings'
import { useAnalytics } from 'use-analytics'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  UseDrawerStateReturn,
} from '../../../../../Drawer'
import { messages } from '../messages'
import { UtmsType } from './CreateCoupon'
import { CouponFields } from './Fields'
import { useCouponQuery } from './hooks/useCouponQuery'
import { useCouponsMutation } from './hooks/useCouponsMutation'
import styles from './style.css'
import { validationSuite } from './validation'

type Props = {
  drawerState: UseDrawerStateReturn
  utms: UtmsType
}

export type CouponForm = {
  name: string
  limit: {
    checked: boolean
    value: number | undefined
  }
  group: {
    checked: boolean
    value: number | undefined
  }
  utmCampaign: string
  utmSource: string
}

const initialValue: CouponForm = {
  name: '',
  limit: { checked: false, value: undefined },
  group: { checked: false, value: undefined },
  utmCampaign: '',
  utmSource: '',
}

export function Editor(props: Props) {
  const { drawerState, utms } = props
  const { track } = useAnalytics()
  const [searchName, setSearchName] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [config, setConfig] = useState<CouponForm>(
    structuredClone(initialValue)
  )

  const { save } = useCouponsMutation()

  const intl = useIntl()

  const { $t } = intl

  useEffect(() => {
    drawerState.open && track(Event.COUPON_DRAWER_VIEW)
  }, [drawerState.open, track])

  const couponQuery = useCouponQuery({
    id: searchName,
    options: { enabled: false },
  })

  const { drawerForm } = useDrawerSubForm<CouponForm>({
    config,
    isOpen: drawerState.open,
    validationFn: validationSuite(intl, utms, couponQuery),
  })

  const name = useWatch({ form: drawerForm, name: 'name' })

  useEffect(() => {
    setSearchName(name)
  }, [name])

  useEffect(() => {
    !drawerState.open && setConfig(structuredClone(initialValue))
  }, [drawerState.open])

  const onSuccess = () => {
    track(Event.COUPON_CREATE_SUCCESS)
    drawerState.hide()
  }
  const onError = () => {
    track(Event.COUPON_CREATE_ERROR)
  }

  const handleSave = async () => {
    track(Event.COUPON_SAVE_CLICK)
    setIsLoading(true)
    const valid = await drawerForm.trigger()
    setIsLoading(false)

    if (!valid) return

    save(drawerForm.getValues(), {
      onSuccess,
      onError,
    })
  }

  return (
    <Drawer state={drawerState} level={1}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.editor.title)}</DrawerTitle>
        <DismissButton onHide={drawerState.hide} />
      </DrawerHeader>
      <DrawerContent>
        <CouponFields.Name form={drawerForm} />
        <CouponFields.Group form={drawerForm} className={styles.marginGroup} />
        <CouponFields.MaxUsage
          form={drawerForm}
          className={styles.marginMaxUsage}
        />
        <CouponFields.Utms utms={utms} form={drawerForm} />
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={drawerState.hide} />
        <Button variant="primary" onClick={handleSave} loading={isLoading}>
          <FormattedMessage {...messages.editor.create} />
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
